import type { LayerSyncOperation } from '@/shared/api/models'
import { OpEnum } from '@/shared/api/models'
import type { LocalLayer } from '../../layer/types'

/**
 * Fields to include when building sync operation data from a layer.
 * Only mutable fields — read-only computed fields (id, url, etc.) are excluded.
 */
const SYNC_DATA_FIELDS = [
  'layer_type',
  'name',
  'z_index',
  'is_visible',
  'opacity',
  'position_x_mm',
  'position_y_mm',
  'width_mm',
  'height_mm',
  'rotation_degrees',
  'flip_horizontal',
  'flip_vertical',
  'image_asset_type',
  'text_content',
  'text_font_data',
  'text_container_data',
  'shape_type',
  'shape_data',
] as const

/**
 * Build a data object from a layer for sync create/update operations.
 * Includes only mutable fields, and adds asset_s3_key if present.
 */
function buildLayerData(layer: LocalLayer): Record<string, unknown> {
  const data: Record<string, unknown> = {}

  for (const field of SYNC_DATA_FIELDS) {
    if (field in layer && layer[field as keyof LocalLayer] !== undefined) {
      data[field] = layer[field as keyof LocalLayer]
    }
  }

  // Include asset_s3_key for image layers from asset pool
  if (layer._assetS3Key) {
    data.asset_s3_key = layer._assetS3Key
  }

  return data
}

/**
 * Compute the list of sync operations needed to bring server state
 * in line with local state.
 */
export function computeLayerOperations(
  currentLayers: LocalLayer[],
  originalLayers: LocalLayer[],
  deletedLayerIds: number[],
): LayerSyncOperation[] {
  const operations: LayerSyncOperation[] = []

  // Creates: layers with negative IDs (temp IDs)
  for (const layer of currentLayers) {
    if (layer.id < 0) {
      operations.push({
        op: OpEnum.create,
        temp_id: String(Math.abs(layer.id)),
        data: buildLayerData(layer),
      })
    }
  }

  // Updates: layers with positive IDs that differ from original
  for (const layer of currentLayers) {
    if (layer.id <= 0) continue

    const original = originalLayers.find((o) => o.id === layer.id)

    if (!original) continue
    if (JSON.stringify(layer) === JSON.stringify(original)) continue

    // Only send changed fields
    const data: Record<string, unknown> = {}

    for (const field of SYNC_DATA_FIELDS) {
      const key = field as keyof LocalLayer

      if (JSON.stringify(layer[key]) !== JSON.stringify(original[key])) {
        data[field] = layer[key]
      }
    }

    if (Object.keys(data).length > 0) {
      operations.push({
        op: OpEnum.update,
        id: layer.id,
        data,
      })
    }
  }

  // Deletes: server-side layers that were removed locally
  for (const id of deletedLayerIds) {
    operations.push({
      op: OpEnum.delete,
      id,
    })
  }

  return operations
}
